import { Component } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export const Loading = ({ rows = 3, label = "Loading" }) => (
  <div data-testid="loading-state" aria-label={label} className="space-y-3">
    {Array.from({ length: rows }).map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-lg" />)}
  </div>
);

export const Spinner = () => <Loader2 className="h-4 w-4 animate-spin" />;

export const Empty = ({ icon: Icon, title, text, action }) => (
  <div data-testid="empty-state" className="flex flex-col items-start gap-3 rounded-xl border border-dashed p-10">
    {Icon && <Icon className="h-7 w-7 text-accent/70" strokeWidth={1.4} />}
    <h3 className="section-title">{title}</h3>
    {text && <p className="max-w-md text-sm text-muted-foreground">{text}</p>}
    {action}
  </div>
);

export const ErrorState = ({ message, onRetry }) => (
  <div data-testid="error-state" role="alert" className="flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/5 p-6">
    <AlertTriangle className="mt-0.5 h-5 w-5 text-destructive" />
    <div className="space-y-2">
      <p className="text-sm font-medium">{message || "Something went wrong."}</p>
      {onRetry && <Button size="sm" variant="outline" onClick={onRetry} data-testid="error-retry-button">Try again</Button>}
    </div>
  </div>
);

export class ErrorBoundary extends Component {
  state = { error: null };
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error("UI error:", error, info); }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="p-8">
        <ErrorState message={`This section hit a problem: ${this.state.error.message}`} onRetry={() => this.setState({ error: null })} />
      </div>
    );
  }
}

export const PageHeader = ({ eyebrow, title, children }) => (
  <div className="rise mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
    <div>
      {eyebrow && <p className="eyebrow mb-1">{eyebrow}</p>}
      <h1 className="page-title">{title}</h1>
    </div>
    {children && <div className="flex flex-wrap gap-2">{children}</div>}
  </div>
);
